import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { Home, Payment } from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser } from '../../../hooks/useApi';
import { SubscriptionsTable } from '../../../components/admin/StripeSubscriptionsTable';
import { InvoicesTable } from '../../../components/admin/StripeInvoicesTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stripe-tabpanel-${index}`}
      aria-labelledby={`stripe-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `stripe-tab-${index}`,
    'aria-controls': `stripe-tabpanel-${index}`,
  };
}

export const StripeManagement: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Stripe Management</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Payment sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Stripe Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Monitor and manage Stripe subscriptions and invoices
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="stripe management tabs"
            >
              <Tab label="Subscriptions" {...a11yProps(0)} />
              <Tab label="Invoices" {...a11yProps(1)} />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <SubscriptionsTable />
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <InvoicesTable />
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
};
